import { useState, useEffect, useCallback, useRef } from "react";

interface UseGalleryScrollOptions {
  totalPairs: number;
  animationDuration?: number;
}

interface UseGalleryScrollReturn {
  currentIndex: number;
  direction: "up" | "down" | null;
  isAnimating: boolean;
  goToIndex: (index: number) => void;
  navigate: (dir: "up" | "down") => void;
}

export function useGalleryScroll({
  totalPairs,
  animationDuration = 900,
}: UseGalleryScrollOptions): UseGalleryScrollReturn {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState<"up" | "down" | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const touchStartY = useRef(0);
  const lockRef = useRef(false);

  const navigate = useCallback(
    (dir: "up" | "down") => {
      if (lockRef.current || totalPairs <= 1) return;
      lockRef.current = true;
      setIsAnimating(true);
      setDirection(dir);
      setCurrentIndex((prev) => {
        if (dir === "down") return (prev + 1) % totalPairs;
        return (prev - 1 + totalPairs) % totalPairs;
      });
      setTimeout(() => {
        lockRef.current = false;
        setIsAnimating(false);
        setDirection(null);
      }, animationDuration);
    },
    [totalPairs, animationDuration],
  );

  const goToIndex = useCallback(
    (index: number) => {
      if (lockRef.current || index === currentIndex) return;
      lockRef.current = true;
      setIsAnimating(true);
      setDirection(index > currentIndex ? "down" : "up");
      setCurrentIndex(index);
      setTimeout(() => {
        lockRef.current = false;
        setIsAnimating(false);
        setDirection(null);
      }, animationDuration);
    },
    [currentIndex, animationDuration],
  );

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (Math.abs(e.deltaY) < 10) return;
      navigate(e.deltaY > 0 ? "down" : "up");
    };
    const handleTouchStart = (e: TouchEvent) => {
      touchStartY.current = e.touches[0].clientY;
    };
    const handleTouchEnd = (e: TouchEvent) => {
      const diff = touchStartY.current - e.changedTouches[0].clientY;
      if (Math.abs(diff) < 50) return;
      navigate(diff > 0 ? "down" : "up");
    };
    window.addEventListener("wheel", handleWheel, { passive: false });
    window.addEventListener("touchstart", handleTouchStart, { passive: true });
    window.addEventListener("touchend", handleTouchEnd, { passive: true });
    return () => {
      window.removeEventListener("wheel", handleWheel);
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [navigate]);

  return { currentIndex, direction, isAnimating, goToIndex, navigate };
}
